/**
 * Mental Accounting experiment translations
 * Last updated: 2025-08-12 15:58:56
 * Updated by: JulianPolly
 */

export const mentalAccounting = {
    de: {
        title: "Was zählt – und wo: Kopf oder Konto?",
        description: "Menschen gehen mit Geld nicht immer rein rational um – oft spielt es eine Rolle, wofür es gedacht war. Welchen Einfluss hat dieses Denken in 'mentalen Konten' auf unsere Entscheidungen?",
        screens: {
            screen1: {
                title: "Das Streaming-Guthaben",
                content: "Endlich ist die neue Staffel deiner Lieblingsserie erschienen! Sie läuft allerdings exklusiv bei dem neuen Streaminganbieter <b>StreamFlix</b>.<br /><br />Du willst die Staffel unbedingt sehen und hast dir deshalb eine Guthabenkarte für einen Monat StreamFlix im Wert von 10&nbsp;€ gekauft.<br /><br />Doch als du zu Hause die Guthabenkarte einlösen möchtest, stellst du fest, dass du sie verloren hast – die Karte ist spurlos verschwunden.",
                question: "Würdest du 10&nbsp;€ für eine weitere Guthabenkarte bezahlen, um trotzdem die neue Staffel schauen zu können?",
                options: {
                    yes: "Ja, ich würde weitere 10&nbsp;€ für die Guthabenkarte bezahlen.",
                    no: "Nein, ich würde keine weitere Guthabenkarte kaufen."
                }
            },
            screen2: {
                title: "Das verlorene Geld – trotzdem Streaming?",
                content: "Wieder ist eine neue Staffel deiner Lieblingsserie erschienen, exklusiv auf <b>StreamFlix</b>. Du willst sie unbedingt sehen und stehst schon an der Kasse, um dir eine Guthabenkarte für einen Monat StreamFlix (10&nbsp;€) zu kaufen.<br /><br />Plötzlich bemerkst du: Du hast unterwegs 10&nbsp;€ aus deinem Geldbeutel verloren! Zum Glück hast du aber noch weitere 10&nbsp;€ dabei – genug, um die Guthabenkarte trotzdem zu kaufen.<br /><br />",
                question: "Würdest du die 10&nbsp;€ für die Guthabenkarte ausgeben, um trotzdem die neue Staffel streamen zu können?",
                options: {
                    yes: "Ja, ich würde die 10&nbsp;€ für die Guthabenkarte ausgeben.",
                    no: "Nein, ich würde auf die Guthabenkarte verzichten."
                }
            },
            results: {
                title: "Ergebnisse: Wer entscheidet wie?",
                case1: {
                    title: "Fall 1: Verlorene Guthabenkarte",
                    description: "Du hattest bereits eine Guthabenkarte gekauft und hast diese verloren."
                },
                case2: {
                    title: "Fall 2: Verlorener Geldschein",
                    description: "Du hattest vor, eine Karte zu kaufen und hattest dafür 10&nbsp;Euro vorgesehen."
                },
                chartOptions: {
                    yes: "Ja",
                    no: "Nein"
                },
                userInfo: {
                    group1: "Gruppe 1",
                    group2: "Gruppe 2",
                    case1: "Fall 1 (Guthabenkarte verloren)",
                    case2: "Fall 2 (Geldschein verloren)",
                    decision: "Deine Entscheidung:",
                    intro: "Du warst zufällig in <b>{group}</b> und hast <b>{case}</b> gesehen.",
                    noDecision: "Deine Entscheidung konnte nicht eindeutig ermittelt werden."
                },
                loading: "Lade Ergebnisse…"
            },
            explanation: {
                title: "Wie mentale Buchführung unsere Entscheidungen beeinflusst",
                sections: [
                    {
                        title: "Mentale Buchführung im Alltag",
                        content: "Hast du schon mal Geld zum Geburtstag oder zu Weihnachten bekommen – und es direkt für etwas Cooles ausgegeben? Und hast du anderes Geld, das du z. B. durch einen Ferienjob verdient hast, eher zurückgelegt? Dann hast du vermutlich schon unbewusst mentale Buchführung betrieben."
                    },
                    {
                        title: "Ein Euro ist nicht gleich ein Euro?",
                        content: "Die Wissenschaft nennt dieses Verhalten 'Mental Accounting' oder auf Deutsch 'mentale Buchführung'. Damit ist gemeint, dass wir unser Geld in Gedanken auf verschiedene 'Konten' verteilen – je nachdem, woher es kommt oder wofür es gedacht ist. Das kann praktisch sein, führt aber auch dazu, dass wir uns manchmal nicht rational verhalten."
                    },
                    {
                        title: "10 + 10 ≠ 20?",
                        content: "Ein Beispiel: Leon hat 20 € bei einem Gewinnspiel gewonnen. Für ihn ist das ein 'Spaßgeld' – also kauft er sich davon gleich zwei neue Spiele. Die 20 €, die er am Wochenende bei einem Aushilfsjob verdient hat, steckt er dagegen aufs Sparkonto. Aus Sicht der Wirtschaftswissenschaft ist das unlogisch: Denn ein Euro ist immer gleich viel wert, egal ob er geschenkt oder erarbeitet wurde. Wenn das Geld einmal ausgegeben ist, fehlt es – egal auf welchem 'mentalen Konto' es vorher lag."
                    },
                    {
                        title: "Warum machen wir das?",
                        content: "Mentale Buchführung hilft uns, besser mit Geld umzugehen, weil sie Ordnung schafft. Viele Menschen haben (bewusst oder unbewusst) verschiedene Konten im Kopf, zum Beispiel für:"
                    }
                ],
                accountExamples: [
                    "🍕 Alltag (z. B. Essen, Kleidung)",
                    "🎉 Freizeit und Spaß",
                    "✈️ Urlaub",
                    "💸 Notfälle"
                ],
                accountExplanation: "Das kann helfen, die eigenen Ausgaben zu planen. Aber es kann auch dazu führen, dass wir irrational entscheiden – zum Beispiel mehr Geld für Spaß ausgeben, weil 'auf dem Freizeitkonto noch etwas übrig' ist, obwohl wir insgesamt kaum noch Geld haben.",
                researchExample: {
                    title: "Ein bekanntes Beispiel aus der Forschung",
                    content: "In einem bekannten Experiment des Ökonomen Richard Thaler sollten Menschen sich vorstellen, sie hätten eine Eintrittskarte für ein Kino gekauft. Einige verloren später ihre Kinokarte, andere verloren einen Geldschein im Wert des Tickets. Anschließend wurden beide Gruppen gefragt, ob sie noch einmal eine Karte kaufen würden.",
                    result: "Das Ergebnis: Obwohl beide Gruppen denselben finanziellen Verlust hatten, waren nur <b>46 %</b> derjenigen, die die Kinokarte verloren hatten, bereit, ein neues Ticket zu kaufen. In der Gruppe, die den Geldschein verloren hatte, entschieden sich dagegen <b>88 %</b> für den erneuten Kauf der Karte.",
                    explanation: "Die erste Gruppe betrachtete die neue Karte als zusätzliche Ausgabe für den Kinobesuch ('Mein Budget für Kino ist schon aufgebraucht'), während die zweite Gruppe den Verlust des Geldscheins als unabhängig vom geplanten Kinobesuch betrachtete.",
                    conclusion: "Das Experiment zeigt anschaulich, wie mentale Buchführung unser Ausgabeverhalten verzerren kann."
                },
                experimentComparison: "Du hast sicherlich schon bemerkt, dass unser Experiment ganz ähnlich aufgebaut war. Ein Teil der Klasse wurde in die Situation versetzt, dass sie die Guthabenkarte für den Streaminganbieter verloren hat, ein Teil von euch hat stattdessen den Geldschein verloren.",
                sources: {
                    title: "Quellen:",
                    items: [
                        "Thaler, R. (1985). Mental accounting and consumer choice. Marketing Science, 4(3), 199–214.",
                        "Heath, C., & Soll, J. B. (1996). Mental budgeting and consumer decisions. Journal of Consumer Research, 23(1), 40–52."
                    ]
                }
            }
        }
    },
    en: {
        title: "What Counts - and Where: Mind or Mental Account?",
        description: "People don't always handle money purely rationally. What often matters is what the money was intended for. What influence does this thinking in 'mental accounts' have on our decisions?",
        screens: {
            screen1: {
                title: "The Streaming Subscription",
                content: "Finally, the new season of your favorite series has been released! However, it's running exclusively on the new streaming provider <b>StreamFlix</b>.<br /><br />You really want to watch the season, so you have bought a prepaid card for one month of StreamFlix worth $10.<br /><br />As you want to redeem the prepaid card at home, you realize you've lost it - the card has disappeared without a trace.",
                question: "Would you pay $10 for another prepaid card to still be able to watch the new season?",
                options: {
                    yes: "Yes, I would pay another $10 for the prepaid card.",
                    no: "No, I would not buy another prepaid card."
                }
            },
            screen2: {
                title: "The Lost Money - Still Streaming?",
                content: "Finally, the new season of your favorite series has been released! However, it's running exclusively on the new streaming provider <b>StreamFlix</b>.<br /><br />. You really want to watch it and are already at the checkout to buy a prepaid card of StreamFlix for one month ($10).<br /><br />Suddenly you notice: You've lost $10 from your wallet on the way! Fortunately, you still have another $10 with you - enough to still buy the prepaid card.<br /><br />",
                question: "Would you spend these $10 on the prepaid card to be able to stream the new season?",
                options: {
                    yes: "Yes, I would spend these $10 on the prepaid card.",
                    no: "No, I wouldn't buy the prepaid card."
                }
            },
            results: {
                title: "Results: Who Decides in What Way?",
                case1: {
                    title: "Case 1: Lost Prepaid Card",
                    description: "You had already bought a prepaid card and lost it."
                },
                case2: {
                    title: "Case 2: Lost Bill",
                    description: "You were planning to buy a card and lost $10 you had set aside for it."
                },
                chartOptions: {
                    yes: "Yes",
                    no: "No"
                },
                userInfo: {
                    group1: "Group 1",
                    group2: "Group 2",
                    case1: "Case 1 (Lost prepaid card)",
                    case2: "Case 2 (Lost bill)",
                    decision: "Your decision:",
                    intro: "You were randomly in <b>{group}</b> and saw <b>{case}</b>.",
                    noDecision: "Your decision could not be clearly determined."
                },
                loading: "Loading results…"
            },
            explanation: {
                title: "How Mental Accounting Influences Our Decisions",
                sections: [
                    {
                        title: "Mental Accounting in Everyday Life",
                        content: "Have you ever received money for your birthday or Christmas and immediately spent it on something cool? And have you saved money that you earned through, for example, a summer job? Then you've already unconsciously practiced mental accounting."
                    },
                    {
                        title: "One Dollar is Not Equal to Another Dollar?",
                        content: "Science calls this behavior Mental Accounting. It means that we distribute our money in our minds to different 'accounts' - depending on where it comes from or what it is intended for. This can be practical, but it also sometimes leads us to not behaving rationally."
                    },
                    {
                        title: "10 + 10 ≠ 20?",
                        content: "An example: Leon won $20 in a competition. For him, this is 'fun money' - so he immediately buys two new games with it. On the other hand, the $20 he earned on the weekend through a part-time job he puts into his savings account. From an economic perspective, this is illogical, because one dollar is always worth the same, regardless of whether it was gifted or earned. Once the money is spent, it is gone - regardless of which mental account it was previously in."
                    },
                    {
                        title: "Why Do We Do This?",
                        content: "Mental accounting helps us to better manage money because it creates order. Many people have (consciously or unconsciously) different accounts in mind, for example for:"
                    }
                ],
                accountExamples: [
                    "🍕 Everyday Life (e.g., food, clothing)",
                    "🎉 Leisure and Fun",
                    "✈️ Vacation",
                    "💸 Emergencies"
                ],
                accountExplanation: "This can help to plan one's expenses. But it can also lead us to make irrational decisions - for example, spending more money on fun because 'there is still something left in the leisure account', although we have hardly any money overall.",
                researchExample: {
                    title: "A Well-Known Example from Research",
                    content: "In a well-known experiment by the economist Richard Thaler, people were asked to imagine that they had bought a ticket for cinema. Some later lost their cinema ticket, others lost a bill worth the ticket. Both groups were then asked if they would buy a ticket again.",
                    result: "The result: Although both groups had the same financial loss, only <b>46%</b> of those who had lost the cinema ticket were willing to buy a new ticket. In the group that had lost the bill, <b>88%</b> decided to buy the ticket again.",
                    explanation: "The first group viewed the new ticket as an additional expense for the cinema visit ('My budget for cinema is already exhausted'), while the second group viewed the loss of the bill as independent of the planned cinema visit.",
                    conclusion: "The experiment shows how mental accounting can distort our spending behavior."
                },
                experimentComparison: "You've probably already noticed that our experiment was set up quite similarly. Part of the class was put in the situation that they had lost the prepaid card for the streaming provider, part of you instead had lost the bill.",
                sources: {
                    title: "Sources:",
                    items: [
                        "Thaler, R. (1985). Mental accounting and consumer choice. Marketing Science, 4(3), 199–214.",
                        "Heath, C., & Soll, J. B. (1996). Mental budgeting and consumer decisions. Journal of Consumer Research, 23(1), 40–52."
                    ]
                }
            }
        }
    }
};